// src/services/StatusService.js
//
// Status overrides: vacation, business trip, working from home, and so on.
//
// A door reader can tell you someone is not in the building. It cannot tell you
// why. These are the things only the person knows, so they set them themselves.
//
// Keyed on the Entra object id server-side, taken from the signed-in token. The
// frontend never sends an identity, which is why none of these calls take one.

import apiClient from './apiClient'

// What a status is called, how it reads, and which icon represents it.
//
// This used to be a hardcoded list here and a matching array in
// service_layer_status.cpp, which had to be edited together. They are now rows
// in a table, editable under Admin > Statuses.
//
// The five below are a FALLBACK, not the definition: they are what the table is
// seeded with, and they exist so that a component rendering before the fetch
// completes shows names rather than raw codes. Once loadStatusTypes has run,
// the table wins.
const SEEDED = [
  { value: 'vacation',       label: 'Vacation',             icon: '\u{1F334}' },
  { value: 'business_trip',  label: 'Business Trip',        icon: '\u{2708}\u{FE0F}' },
  { value: 'work_from_home', label: 'Work from Home',       icon: '\u{1F3E0}' },
  { value: 'course',         label: 'Course',               icon: '\u{1F393}' },
  { value: 'other_absence',  label: 'Other absence (Paid)', icon: '\u{1F4C5}' }
]

let loaded = null

/**
 * The statuses, from the table.
 *
 * Cached after the first call: several components ask for them and they change
 * about once a year. Call refreshStatusTypes after editing them.
 */
export async function getStatusTypes({ activeOnly = true } = {}) {
  const response = await apiClient.post('/GetStatusTypes', { active_only: activeOnly })

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not read statuses')
  }

  const types = (response.data.types ?? []).map((t) => ({
    value: t.code,
    label: t.label,
    icon: t.icon,
    position: t.position,
    active: t.active !== false,
    // Whether setting this status asks for working hours. A flag rather than a
    // hardcoded code, so a renamed or newly added remote-working status behaves
    // correctly without anybody editing the app.
    asksForHours: t.asks_for_hours === true
  }))

  // Only the active list becomes the cache statusMeta reads. The admin page
  // asks for everything, and a switched-off status should not reappear in a
  // dropdown because the admin page happened to load first.
  if (activeOnly) loaded = types

  return types
}

export async function setStatusType(type) {
  const response = await apiClient.post('/SetStatusType', {
    code: type.value ?? '',
    label: type.label,
    icon: type.icon ?? '',
    position: type.position ?? 0,
    active: type.active !== false,
    asks_for_hours: type.asksForHours === true
  })

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not save the status')
  }
  return response.data.code
}

export async function deleteStatusType(code) {
  const response = await apiClient.post('/DeleteStatusType', { code })

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not delete the status')
  }
}

/** The cached list, or the seeded one before anything has loaded. */
export function statusTypes() {
  return loaded ?? SEEDED
}

export function statusMeta(value) {
  return statusTypes().find((s) => s.value === value) ?? null
}

/** Every status covering today, keyed by Entra object id for lookup. */
export async function getStatusesToday() {
  const response = await apiClient.post('/GetStatusesToday', {})

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not read statuses')
  }

  const map = {}
  for (const row of response.data.statuses ?? []) {
    // First one wins if somebody has overlapping entries. Rather than picking a
    // winner arbitrarily on the display, it takes the earliest-created, which is
    // the order the query returns.
    if (!map[row.entra_object_id]) map[row.entra_object_id] = row
  }
  return map
}

/**
 * Every status overlapping a date range, for everyone.
 *
 * Used by the person log and the reports to show a day somebody worked from
 * home or was away, which produces no badge events and would otherwise look
 * like a day they did not turn up.
 *
 * Each row carries the person's name, resolved server-side from the badge
 * mapping, because a status records only an email.
 */
export async function getStatusesRange(from, to, companyGroup = 0) {
  // companyGroup is a CompanyGroup id; 0 or absent means everyone.
  const body = { from, to }
  if (companyGroup) body.company_group = companyGroup
  const response = await apiClient.post('/GetStatusesRange', body)

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not read statuses')
  }
  return response.data.statuses ?? []
}

export async function getMyStatuses() {
  const response = await apiClient.post('/GetMyStatuses', {})

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not read your statuses')
  }
  return response.data.statuses ?? []
}

export async function setMyStatus(status, startDate, endDate, startTime = '', endTime = '') {
  const response = await apiClient.post('/SetMyStatus', {
    status,
    start_date: startDate,
    end_date: endDate,
    // Empty for a status that does not ask for hours. The backend stores NULL
    // rather than a misleading 00:00.
    start_time: startTime,
    end_time: endTime
  })

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not save the status')
  }
}

export async function deleteMyStatus(id) {
  const response = await apiClient.post('/DeleteMyStatus', { id })

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not remove the status')
  }
}

/**
 * Close an entry that was started with only a start time.
 *
 * Somebody working from home records when they started and gets on with the
 * day. The end time is only known when the day ends, and asking for it in the
 * morning meant guessing.
 */
export async function setMyStatusEndTime(id, endTime) {
  const response = await apiClient.post('/SetMyStatusEndTime', {
    id,
    end_time: endTime
  })

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not save the end time')
  }
}
